import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { CropFreeOutlined, PlaceOutlined, TimelineOutlined } from "@mui/icons-material";

import {
  GoogleMap,
  useLoadScript,
  Autocomplete as GAutocomplete,
  Marker,
  Polygon
} from "@react-google-maps/api";
import { monochromeDarkStyle } from "../../customProject/constants/customStylesMap";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: 15
};

const defaultCenter = { lat: 20.6736, lng: -103.344 }; // Guadalajara centro
const LIBRARIES = ["places"];

function parseDefaultValue(defaultValue) {
  if (!defaultValue) return null;
  try {
    if (typeof defaultValue === "string") {
      return JSON.parse(defaultValue);
    }
    return defaultValue;
  } catch (e) {
    console.warn("Valor de ubicación no es JSON válido", e);
    return null;
  }
}

export default function LocationFieldGoogle({
  id,
  label = "Ubicación",
  apiKey = process.env.REACT_APP_GMAPS_KEY,
  defaultValue
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("point"); // "point" | "polygon"
  const [center, setCenter] = useState(defaultCenter);
  const [point, setPoint] = useState(null);
  const [polygon, setPolygon] = useState([]);
  const [displayValue, setDisplayValue] = useState("");
  const [autocomplete, setAutocomplete] = useState(null);
  const [rawValue, setRawValue] = useState(defaultValue || "");

  // solo para usar en handleOpen (no en useEffect)
  const parsedDefault = parseDefaultValue(defaultValue);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
    id: "location-field-gmaps"
  });

  useEffect(() => {
    setRawValue(defaultValue || "");
  }, [defaultValue]);

  const buildDisplayText = (val) => {
    if (!val) return "";
    if (val.type === "point" && val.point) {
      return `Punto: ${val.point.lat.toFixed(5)}, ${val.point.lng.toFixed(5)}`;
    }
    if (val.type === "polygon" && val.polygon && val.polygon.length > 0) {
      return `Zona: ${val.polygon.length} vértices`;
    }
    return "";
  };

  // ⬇️ AQUÍ EL CAMBIO IMPORTANTE
  // Inicializar centro/punto/polígono cuando CAMBIA defaultValue
  useEffect(() => {
    const parsed = parseDefaultValue(defaultValue);
    if (!parsed) return;

    const initialCenter = parsed.center || parsed.point || defaultCenter;
    setCenter(initialCenter);
    setPoint(parsed.point || null);
    setPolygon(parsed.polygon || []);
    setMode(parsed.type || "point");
    setDisplayValue(buildDisplayText(parsed));
  }, [defaultValue]);

  const handleOpen = () => {
    if (parsedDefault) {
      setCenter(parsedDefault.center || parsedDefault.point || defaultCenter);
      setPoint(parsedDefault.point || null);
      setPolygon(parsedDefault.polygon || []);
      setMode(parsedDefault.type || "point");
      setDisplayValue(buildDisplayText(parsedDefault));
    } else {
      setCenter(defaultCenter);
      setPoint(null);
      setPolygon([]);
      setMode("point");
      setDisplayValue("");
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      if (mode === "point") {
        setPoint({ lat, lng });
        setCenter({ lat, lng });
        setPolygon([]);
      } else if (mode === "polygon") {
        setPolygon((prev) => [...prev, { lat, lng }]);
      }
    },
    [mode]
  );

  const handleClear = () => {
    setPoint(null);
    setPolygon([]);
  };

  const handleAccept = () => {
    const valueObj = {
      type: mode,
      center: center,
      point: mode === "point" ? point : null,
      polygon: mode === "polygon" ? polygon : []
    };
    const json = JSON.stringify(valueObj);

    setRawValue(json);
    setDisplayValue(buildDisplayText(valueObj));
    setOpen(false);
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (place && place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setCenter({ lat, lng });
      if (mode === "point") {
        setPoint({ lat, lng });
      }
    }
  };

  if (loadError) {
    console.error("Error cargando Google Maps");
  }

  return (
    <>
      <input id={id} type="hidden" value={rawValue} readOnly />

      <Box style={{ width: "100%", position: "relative", marginTop: 8 }}>
        {isLoaded ? (
          <>
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: 150,
                borderRadius: 15
              }}
              center={center}
              zoom={15}
              options={{
                styles: monochromeDarkStyle
              }}
            >
              {point && mode === "point" && <Marker position={point} />}

              {polygon && polygon.length > 0 && (
                <Polygon
                  path={polygon}
                  options={{
                    fillColor: "#1976d2",
                    fillOpacity: 0.2,
                    strokeColor: "#1976d2",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    clickable: false,
                    editable: false
                  }}
                />
              )}
            </GoogleMap>
            <Button
              fullWidth
              style={{
                position: "absolute",
                top: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(50,50,50,0.25)",
                borderRadius: 15,
                color: "#fff"
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpen();
              }}
            >
              <CropFreeOutlined />
              &nbsp;Editar ubicación / zona
            </Button>
          </>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleOpen}
            style={{ borderRadius: 15, height: 150 }}
          >
            Cargar mapa de ubicación
          </Button>
        )}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Seleccionar ubicación</DialogTitle>
        <DialogContent>
          {!isLoaded ? (
            <Box padding={2}>Cargando mapa…</Box>
          ) : (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box flex={1}>
                  <GAutocomplete
                    onLoad={(auto) => setAutocomplete(auto)}
                    onPlaceChanged={onPlaceChanged}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Buscar lugar o dirección…"
                      autoComplete="off"
                    />
                  </GAutocomplete>
                </Box>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(e, val) => {
                    if (val) setMode(val);
                  }}
                  size="small"
                >
                  <ToggleButton value="point">
                    <PlaceOutlined fontSize="small" />
                    &nbsp;Punto
                  </ToggleButton>
                  <ToggleButton value="polygon">
                    <TimelineOutlined fontSize="small" />
                    &nbsp;Zona
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button onClick={handleClear} size="small">
                  Limpiar
                </Button>
              </Stack>

              <Box>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={15}
                  onClick={handleMapClick}
                  options={{
                    styles: monochromeDarkStyle
                  }}
                >
                  {point && mode === "point" && <Marker position={point} />}

                  {polygon && polygon.length > 0 && (
                    <Polygon
                      path={polygon}
                      options={{
                        fillColor: "#1976d2",
                        fillOpacity: 0.2,
                        strokeColor: "#1976d2",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        clickable: false,
                        editable: false
                      }}
                    />
                  )}
                </GoogleMap>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleAccept} variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
